import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

function useUserDetails(isLoggedIn) {
  return useQuery({
    queryKey: ['user', 'user-details'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get('/api/user', { signal });
      return data;
    },
    enabled: isLoggedIn,
    retry: false,
  });
}

const aqtPotApiUrl = '/api/aqt-pot-api';

function useSourceDetails(resource, id) {
  return useQuery({
    queryKey: ['source', resource, id],
    queryFn: async ({ signal }) => {
      const endpoint = `${aqtPotApiUrl.replace(/\/$/, '')}/${resource}/${encodeURIComponent(String(id))}`;
      const { data } = await axios.get(endpoint, { signal });
      return data;
    },
    enabled: Boolean(id),
    retry: false,
  });
}

export function useFlowingWaterDetails(id) {
  return useSourceDetails('stromend-water', id);
}

export function useFlowingWaterSegments(id) {
  return useQuery({
    queryKey: ['flowing-water-segments', id],
    queryFn: async ({ signal }) => {
      const endpoint = `${aqtPotApiUrl}/stromend-water-stukje-100m?stromendWaterId=${encodeURIComponent(String(id))}`;
      const { data } = await axios.get(endpoint, { signal });
      return Array.isArray(data) ? data : data?.features || [];
    },
    enabled: Boolean(id),
    retry: false,
  });
}

export function useStillWaterDetails(id) {
  return useSourceDetails('stilstaand-water', id);
}

function useDeltaTimeseries(resource, id) {
  return useQuery({
    queryKey: ['delta-t', resource, id],
    queryFn: async ({ signal }) => {
      const endpoint = `${aqtPotApiUrl}/${resource}/${encodeURIComponent(String(id))}/delta-t`;
      const { data } = await axios.get(endpoint, { signal });
      return data;
    },
    enabled: Boolean(id),
    retry: false,
  });
}

export function useFlowingWater100mTimeseries(id) {
  return useDeltaTimeseries('stromend-water-stukje-100m', id);
}

export function useStillWaterCellTimeseries(id) {
  return useDeltaTimeseries('stilstaand-water-cel', id);
}

export function useStillWaterCells(id) {
  return useQuery({
    queryKey: ['still-water-cells', id],
    queryFn: async ({ signal }) => {
      const endpoint = `${aqtPotApiUrl}/stilstaand-water/${encodeURIComponent(String(id))}/stilstaand-water-cellen`;
      const { data } = await axios.get(endpoint, { signal });
      return data;
    },
    enabled: Boolean(id),
    retry: false,
  });
}

export function useSewageWaterDetails(id) {
  return useSourceDetails('rwzi', id);
}

export function useCollectorDetails(id) {
  return useSourceDetails('collector', id);
}

function useMunicipalityDetails(municipalityId) {
  return useQuery({
    queryKey: ['municipality', municipalityId],
    queryFn: async ({ signal }) => {
      const endpoint = `${aqtPotApiUrl.replace(/\/$/, '')}/gemeente/${encodeURIComponent(String(municipalityId))}`;
      const { data } = await axios.get(endpoint, { signal });
      return data;
    },
    enabled: Boolean(municipalityId),
    retry: false,
  });
}

export function useStatisticalSectorDetails(statisticalSectorId) {
  return useQuery({
    queryKey: ['statistical-sector', statisticalSectorId],
    queryFn: async ({ signal }) => {
      const endpoint = `${aqtPotApiUrl.replace(/\/$/, '')}/stat-sector/${encodeURIComponent(String(statisticalSectorId))}`;
      const { data } = await axios.get(endpoint, { signal });
      return data;
    },
    enabled: Boolean(statisticalSectorId),
    retry: false,
  });
}

export function useMunicipalityNames(municipalitySamples) {
  const samples = [...new Map(
    (municipalitySamples || [])
      .filter(sample => sample?.municipalityId)
      .map(sample => [String(sample.municipalityId), sample.sectorCode]),
  ).entries()]
    .map(([municipalityId, sectorCode]) => ({ municipalityId, sectorCode }))
    .sort((a, b) => a.municipalityId.localeCompare(b.municipalityId));

  return useQuery({
    queryKey: ['municipality-names', samples],
    queryFn: async ({ signal }) => {
      const getGemeenteName = async (id) => {
        const endpoint = `${aqtPotApiUrl}/gemeente/${encodeURIComponent(id)}`;
        const { data } = await axios.get(endpoint, { signal });
        return data?.Naam ?? data?.naam ?? null;
      };

      const entries = await Promise.all(
        samples.map(async ({ municipalityId, sectorCode }) => {
          try {
            return [municipalityId, await getGemeenteName(municipalityId)];
          } catch {
            // The first 5 characters are not always a valid gemeente id
            // (e.g. industrial zones), so fall back to the sector's gemeente.
            try {
              const sectorEndpoint = `${aqtPotApiUrl}/stat-sector/${encodeURIComponent(sectorCode)}`;
              const { data: sector } = await axios.get(sectorEndpoint, { signal });
              const gemeenteId = sector?.ID_gemeente;
              if (gemeenteId === undefined || gemeenteId === null) return [municipalityId, null];
              return [municipalityId, await getGemeenteName(gemeenteId)];
            } catch {
              return [municipalityId, null];
            }
          }
        }),
      );

      return Object.fromEntries(entries);
    },
    enabled: samples.length > 0,
    retry: false,
  });
}

export {
  useMunicipalityDetails,
  useUserDetails,
};
